import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtAUD } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Check } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [record, setRecord] = useState(null);
  const attempts = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    const poll = async () => {
      if (stop || attempts.current > 8) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setRecord(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        console.error(e);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="pt-40 pb-24 px-6 md:px-10 min-h-[80svh]" data-testid="payment-success-page">
      <div className="max-w-2xl">
        <div className="overline mb-6">Ξ 008 — Transmission</div>
        {status === "polling" && (
          <>
            <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">Verifying…</h1>
            <p className="mt-8 text-[#888]">Confirming secure transaction with Stripe. This takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="inline-flex items-center gap-3 border border-[#EAEAEA] px-4 py-2 mb-8">
              <Check size={16} /> <span className="overline">Order confirmed</span>
            </div>
            <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">
              Your vault is<br />being prepared.
            </h1>
            <p className="mt-8 text-[#888] max-w-lg">
              Your device is entering hardening in our Sydney facility. You will receive a confirmation email
              within the next few minutes and a shipping notification within 5–7 business days.
            </p>
            {record && (
              <div className="mt-12 border border-[#1A1A1A] p-6 max-w-md">
                <div className="overline mb-3">Reference</div>
                <div className="font-mono text-sm break-all">{sessionId}</div>
                <div className="overline mt-6 mb-3">Amount</div>
                <div className="font-display text-3xl">{fmtAUD((record.amount || 0) / 100)}</div>
              </div>
            )}
            <div className="mt-12 flex gap-4">
              <Link to="/" className="btn-vault">Return home</Link>
              <Link to="/shop" className="btn-ghost">Continue browsing →</Link>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9] text-[#7C3AED]">Transmission failed.</h1>
            <p className="mt-8 text-[#888]">Payment could not be verified. No charge has been made.</p>
            <Link to="/cart" className="btn-vault danger mt-12">Return to cart</Link>
          </>
        )}
      </div>
    </div>
  );
}
